import React, { useState, useEffect, useRef } from "react";
import OtpInput from "react-otp-input";
import axios from "axios";
import { baseUrl } from "../../helper/urls";
import { Loader2 } from "lucide-react";
import { Country, State, City } from "country-state-city";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import ImageUpload from "../../components/child/ImageUpload";
import { Icon } from "@iconify/react/dist/iconify.js";
import { Link } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  Button,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useSelector } from "react-redux";
import Modal from "@mui/material/Modal";
const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 1200,
  height: 600,
  maxHeight: "80vh",
  overflowY: "auto",
  bgcolor: "background.paper",
  border: "2px solid #000",
  boxShadow: 24,
  p: 4,
};

const CounselorProfilePage = () => {
  const { token } = useSelector((state) => state.auth);
  const [currentStep, setCurrentStep] = useState(1);
  const [isEntity, setIsEntity] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [isChecked, setIsChecked] = useState(false);
  const [address, setAddress] = useState("");
  const [country, setCountry] = useState("US"); // Default to USA
  const [city, setCity] = useState("");
  const [stateName, setStateName] = useState("");
  const [zipCode, setZipCode] = useState("");
  const [id, setId] = useState("");
  const [originalDetails, setOriginalDetails] = useState(null);
  const [docData, setDocData] = useState("");
  const [loading, setLoading] = useState(false);

  const [policyLoading, setPolicyLoading] = useState(false);
  const [selectedPolicy, setSelectedPolicy] = useState("");
  const [modalTitle, setModalTitle] = useState("");
  const [showModal, setShowModal] = useState(false);

  const [personalDetails, setPersonalDetails] = useState({
    firstName: "",
    lastName: "",
    email: "",
    countryCode: "",
    phone: "",
    universityName: "",
    satActScore: "",
    majorCourse: "",
    interestedIn: "",
    testimony: "",
  });

  const editableFields = [
    "satActScore",
    "majorCourse",
    "interestedIn",
    "testimony",
  ];

  // Country, State, City data
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);

  // Step 1: Personal Details
  const [personalDetailsLoading, setPersonalDetailsLoading] = useState(false);

  // Step 2: Documents
  const [profileImage, setProfileImage] = useState(null);
  const [workAuthDocument, setWorkAuthDocument] = useState(null);
  // const [ndaDocument, setNdaDocument] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [profileError, setProfileError] = useState("");
  const [workAuthError, setWorkAuthError] = useState("");
  const [ndaError, setNdaError] = useState("");
  const [profileImageUrl, setProfileImageUrl] = useState("");
  const [workAuthUrl, setWorkAuthUrl] = useState("");
  const [ndaUrl, setNdaUrl] = useState("");
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [fileNames, setFileNames] = useState([]);
  // Step 3: Bank Details
  const [imagePreview, setImagePreview] = useState(null);
  const [bankData, setBankData] = useState({
    bankName: "",
    routingNumber: "",
    accountNo: "",
    confirmAccountNo: "",
    accountType: "",
    cancelledCheque: null,
  });
  const [bankId, setBankId] = useState(null);
  const [bankLoading, setBankLoading] = useState(false);
  const [uploadedChequeUrl, setUploadedChequeUrl] = useState("");

  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 3MB

  // Initialize country, state, city data
  useEffect(() => {
    // Get all countries
    const allCountries = Country.getAllCountries();
    setCountries(allCountries);

    // Set default states for USA
    const usaStates = State.getStatesOfCountry("US");
    setStates(usaStates);
  }, []);
  const fileInputRef = useRef(null);

  const removeImage = () => {
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = null;
    }
  };

  useEffect(() => {
    return () => {
      if (workAuthUrl) {
        URL.revokeObjectURL(workAuthUrl);
      }
    };
  }, [workAuthUrl]);
  const handleCheckboxChange = (event) => {
    setIsChecked(event.target.checked);
  };
  // Update states when country changes
  useEffect(() => {
    if (country) {
      const countryStates = State.getStatesOfCountry(country);
      setStates(countryStates);
      setStateName(""); // Reset state when country changes
      setCity(""); // Reset city when country changes
      setCities([]); // Reset cities list
    }
  }, [country]);

  // Update cities when state changes
  useEffect(() => {
    if (country && stateName) {
      const stateCities = City.getCitiesOfState(country, stateName);
      setCities(stateCities);
    }
  }, [country, stateName]);

  // Navigation functions
  const nextStep = () => {
    if (currentStep < 5) {
      setCurrentStep(currentStep + 1);
    }
  };
  const removeFileName = (name) => {
    setFileNames((prev) => prev.filter((fileName) => fileName !== name));
  };
  const prevStep = async () => {
    if (currentStep > 1) {
      // Fetch data when going back to ensure it's up to date
      if (currentStep === 4) {
        // Going back to documents step
        await fetchDocuments();
      } else if (currentStep === 3) {
        // Going back to personal details step
        await getPersonalDetails();
      }

      setCurrentStep(currentStep - 1);
    }
  };

  // Initialize data on component mount
  useEffect(() => {
    viewProfileData();
  }, []);

  // ID Number input handling
  const getInputLength = () => {
    if (isEntity === "yes") return 9; // EIN: 777-77-7777
    if (isEntity === "no") return 9; // SSN: 11-2222222
    return 0;
  };

  const getSeparators = () => {
    if (isEntity === "yes") return [1]; // EIN: after 3rd and 5th
    if (isEntity === "no") return [2, 4]; // SSN: after 2nd
    return [];
  };

  const handleChange = (value) => {
    if (/^\d*$/.test(value) && value.length <= getInputLength()) {
      setIdNumber(value);
    }
  };

  const handleChanges = (e) => {
    const { name, value } = e.target;
    if (editableFields.includes(name)) {
      setPersonalDetails((prev) => ({
        ...prev,
        [name]: value,
      }));
    }
  };

  const renderMaskedInput = (inputProps, index) => {
    const masked = idNumber.length === getInputLength() && index < 5;
    return (
      <input
        {...inputProps}
        value={masked ? "*" : idNumber[index] || ""}
        style={{
          width: "45px",
          height: "45px",
          textAlign: "center",
          fontSize: "25px",
          margin: "0 4px",
          border: "1px solid #ccc",
          borderRadius: "6px",
          padding: "14px",
        }}
      />
    );
  };

  const renderFormattedInput = () => {
    const numInputs = getInputLength();
    const separators = getSeparators();
    const label =
      isEntity === "no"
        ? "SSN (Format: 777-77-7777)"
        : "EIN(Format: 11-2222222)";

    return (
      <div className="col-6 mt-2">
        <label className="form-label mb-0">
          {label}
          <span className="text-danger-600">*</span>
        </label>
        {/* <label className="">{label}</label> */}
        <OtpInput
          value={idNumber}
          onChange={handleChange}
          numInputs={numInputs}
          isInputNum
          renderSeparator={(index) =>
            separators.includes(index) ? (
              <span style={{ margin: "0 2px" }}>-</span>
            ) : null
          }
          renderInput={renderMaskedInput}
        />
      </div>
    );
  };

  const viewProfileData = async () => {
    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };

    try {
      const response = await axios.get(
        `${baseUrl}/api/counselors/getCounselorByToken`,
        { headers }
      );
      const profileData = response.data.data;

      if (profileData?.profileImage) {
        let image = `${baseUrl}${profileData?.profileImage}`;
        console.log("image::::::::::::::::::::;", image);
        setImagePreview(image); // show API image
      }

      if (profileData) {
        setPersonalDetails({
          firstName: profileData.firstName || "",
          lastName: profileData.lastName || "",
          email: profileData.email || "",
          countryCode: profileData.country_code || "",
          phone: profileData.phone || "",
          universityName: profileData.universityName || "",
          satActScore: profileData.satActScore || "",
          majorCourse: profileData.majorCourse || "",
          interestedIn: profileData.interestedIn || "",
          testimony: profileData.testimony || "",
        });
      }
    } catch (error) {
      console.error("Error fetching profile data:", error);
    } finally {
      setLoading(false); // Stop loader regardless of success or error
    }
  };

  const handlePersonalUpdate = async (e) => {
    e.preventDefault();

    const payload = {
      satActScore: personalDetails.satActScore,
      interestedIn: personalDetails.interestedIn,
      testimony: personalDetails.testimony,
      majorCourse: personalDetails.majorCourse,
    };

    try {
      const response = await axios.post(
        `${baseUrl}/api/counselors/update`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Axios auto-throws for non-2xx, so this is safe
      toast.success("Counselor updated successfully!");
      console.log("API result:", response.data);
    } catch (error) {
      console.error("Update error:", error.response?.data || error.message);
      alert("Failed to update counselor.");
    }
  };

  // STEP 1: Personal Details API Functions
  useEffect(() => {
    if (states.length > 0) {
      getPersonalDetails(states);
    }
  }, [states]);
  const getPersonalDetails = async (states) => {
    setPersonalDetailsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/counselors/getPersonalDetails`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("API Response:", response.data);
      const data = response.data.data;

      if (data) {
        console.log("Setting form data from API:", data);
        setId(data?.id);
        setOriginalDetails(data); // Store original for comparison

        // Set address and zip code
        setAddress(data.address || "");
        setZipCode(data.zipCode || "");

        // Set country (default to US if not provided)
        const countryCode = data.country || "US";
        setCountry(countryCode);
        console.log("state:::::::::::::::::::::", states);
        // Set state code
        if (data.state) {
          const selectedState = states.find((s) => s.name === data.state);
          const fullStateName = selectedState
            ? selectedState.isoCode
            : stateName;
          console.log(
            ";;;;;;;;;;;;;;;eeeee;;;;;;",
            selectedState,
            fullStateName,
            "lllllllllllll",
            states
          );

          setStateName(fullStateName);
          // setStateName(data.state);
        }

        // Set city
        if (data.city) {
          setCity(data.city);
        }

        // Set entity type and ID number
        if (data.EIN) {
          setIsEntity("yes");
          setIdNumber(data.EIN);
        } else if (data.SSN) {
          setIsEntity("no");
          setIdNumber(data.SSN);
        }
      }
    } catch (error) {
      console.error("Error fetching personal details:", error);
      // toast.error("Failed to load personal details");
    } finally {
      setPersonalDetailsLoading(false);
    }
  };

  const submitPersonalDetails = async () => {
    setPersonalDetailsLoading(true);

    // Find the full state name from the state code
    const selectedState = states.find((s) => s.isoCode === stateName);
    const fullStateName = selectedState ? selectedState.name : stateName;
    console.log(states, selectedState, ";;;;;;;;;;;;;;;;;;;;;;;;;;;s");
    const data = {
      address,
      city,
      zipCode,
      state: fullStateName, // Send state code as received from the API
      country,
      ...(isEntity === "yes" ? { EIN: idNumber } : { SSN: idNumber }),
    };

    console.log("Submitting personal details:", data);

    try {
      const response = await axios.post(
        `${baseUrl}/api/counselors/createPersonalDetails`,
        data,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      console.log("Personal details created:", response.data);
      // Update the ID for future updates
      if (response.data.data && response.data.data.id) {
        setId(response.data.data.id);
      }
      toast.success("Personal details saved successfully!");
      return true;
    } catch (error) {
      console.error("Error creating personal details:", error);
      toast.error("Failed to save personal details");
      return false;
    } finally {
      setPersonalDetailsLoading(false);
    }
  };

  const updatePersonalDetails = async () => {
    setPersonalDetailsLoading(true);
    const selectedState = states.find((s) => s.isoCode === stateName);
    const fullStateName = selectedState ? selectedState.name : stateName;
    console.log(states, fullStateName, ";;;;;;;;;;;;;;;;;;;;;;;;;;;s");
    const payload = {
      address: address || "",
      city: city || "",
      zipCode: zipCode || "",
      state: fullStateName, // Send state code as received from the API
      country: country || "US",
      ...(isEntity === "no" ? { SSN: idNumber } : { EIN: idNumber }),
      id: String(id || ""),
    };

    console.log("Updating personal details:", payload);

    try {
      const response = await axios.post(
        `${baseUrl}/api/counselors/updatePersonalDetails`,
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );
      console.log("Personal details updated:", response.data);
      toast.success("Personal details updated successfully!");
      return true;
    } catch (error) {
      console.error("Error updating personal details:", error);
      toast.error("Failed to update personal details");
      return false;
    } finally {
      setPersonalDetailsLoading(false);
    }
  };

  const isPersonalDataChanged = () => {
    if (!originalDetails) return true; // No data => assume changed

    return (
      address !== (originalDetails.address || "") ||
      city !== (originalDetails.city || "") ||
      stateName !== (originalDetails.state || "") ||
      country !== (originalDetails.country || "US") ||
      zipCode !== (originalDetails.zipCode || "") ||
      (isEntity === "no" && idNumber !== (originalDetails.SSN || "")) ||
      (isEntity === "yes" && idNumber !== (originalDetails.EIN || ""))
    );
  };

  const handlePersonalDetailsNext = async (e) => {
    if (
      !personalDetails.satActScore ||
      !personalDetails.interestedIn ||
      !personalDetails.testimony ||
      !personalDetails.majorCourse
    ) {
      toast.error("Please fill in all required fields");
      return;
    }
    const testimonyWordCount = personalDetails.testimony
      .trim()
      .split(/\s+/)
      .filter(Boolean).length;

    if (testimonyWordCount < 200) {
      toast.error("Testimony must be at least 200 words.");
      return;
    }

    await handlePersonalUpdate(e);
    nextStep();
    // }
  };
  const handleAddressDetailsNext = async () => {
    // Validate required fields
    if (!address || !city || !stateName || !zipCode || !idNumber || !isEntity) {
      toast.error("Please fill in all required fields");
      return;
    }

    // First check if we have the latest data
    // await getPersonalDetails();

    let success = false;
    if (!originalDetails) {
      // No existing data, create new
      success = await submitPersonalDetails();
    } else if (isPersonalDataChanged()) {
      // Data exists but has changed, update it
      success = await updatePersonalDetails();
    } else {
      // Data exists and hasn't changed
      success = true;
    }

    if (success) {
      // Fetch documents data for the next step
      await fetchDocuments();
      nextStep();
    }
  };

  // STEP 2: Documents API Functions
  // function handleProfilePictureChange(e) {
  //   const file = e.target.files[0];
  //   if (file) {
  //     if (file.size > MAX_FILE_SIZE) {
  //       setProfileError("Profile picture must be less than 5MB.");
  //       setProfileImage(null);
  //       setPreviewUrl(null);
  //       return;
  //     }

  //     // Check image dimensions
  //     const img = new Image();
  //     img.onload = () => {
  //       URL.revokeObjectURL(img.src); // Clean up
  //       if (img.width !== 150 || img.height !== 150) {
  //         setProfileError("Profile picture must be exactly 150x150 pixels.");
  //         setProfileImage(null);
  //         setPreviewUrl(null);
  //       } else {
  //         setProfileError("");
  //         setProfileImage(file);
  //         setPreviewUrl(URL.createObjectURL(file));
  //       }
  //     };
  //     img.onerror = () => {
  //       setProfileError("Invalid image file.");
  //       setProfileImage(null);
  //       setPreviewUrl(null);
  //     };
  //     img.src = URL.createObjectURL(file);
  //   }
  // }

  const handleWorkAuthChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setWorkAuthError("Work Authorization must be less than 5MB.");
        setWorkAuthDocument(null);
        setWorkAuthUrl(null);
        setFileNames([]);
      } else {
        setWorkAuthError("");
        setWorkAuthDocument(file);
        setWorkAuthUrl(URL.createObjectURL(file)); // ✅ Create preview URL
        setFileNames([file.name]); // ✅ Set single file name
      }
    }
  };

  // const handleWorkAuthChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     if (file.size > MAX_FILE_SIZE) {
  //       setWorkAuthError("Work Authorization must be less than 5MB.");
  //       setWorkAuthDocument(null);
  //     } else {
  //       setWorkAuthError("");
  //       setWorkAuthDocument(file);
  //       const files = Array.from(e.target.files);
  //       const newFileNames = files?.map((file) => file.name);
  //       setFileNames((prev) => [...prev, ...newFileNames]);
  //     }
  //   }
  // };

  // const handleNdaChange = (e) => {
  //   const file = e.target.files[0];
  //   if (file) {
  //     if (file.size > MAX_FILE_SIZE) {
  //       setNdaError("NDA must be less than 3MB.");
  //       setNdaDocument(null);
  //     } else {
  //       setNdaError("");
  //       setNdaDocument(file);
  //     }
  //   }
  // };

  const fetchDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/counselors/getDocumentByCounsolerId`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const docData = response.data.data;
      setDocData(docData);

      if (docData) {
        // Set preview and document URLs
        if (docData.profileImage) {
          setImagePreview(`${baseUrl}${docData.profileImage}`);
        }
        if (docData.workAuthDocument) {
          setWorkAuthUrl(`${baseUrl}${docData.workAuthDocument}`);
        }
        if (docData.NDA) {
          setNdaUrl(`${baseUrl}${docData.NDA}`);
        }
        if (docData.profile_consent == "1") {
          setIsChecked(true);
        }
      }
    } catch (err) {
      console.error("Error fetching documents:", err);
      toast.error("Failed to load documents");
    } finally {
      setDocumentsLoading(false);
    }
  };

  // const downloadNDA = async () => {
  //   try {
  //     const response = await axios.get(
  //       `${baseUrl}/api/counselors/downloadNDA`,
  //       {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //         },
  //         responseType: "blob", // important for file download
  //       }
  //     );

  //     const url = window.URL.createObjectURL(new Blob([response.data]));
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.setAttribute("download", "NDA.pdf"); // Set your preferred file name
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //   } catch (error) {
  //     console.error("Error downloading NDA:", error);
  //     alert("Failed to download NDA file.");
  //   }
  // };

  const createDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const formData = new FormData();
      if (workAuthDocument) {
        formData.append("workAuthDocument", workAuthDocument);
      }
      // if (ndaDocument) {
      //   formData.append("NDA", ndaDocument);
      // }
      if (profileImage) {
        formData.append("profileImage", profileImage);
      }
      if (isChecked) {
        formData.append("profile_consent", "1");
      } else {
        formData.append("profile_consent", "0");
      }

      const response = await axios.post(
        `${baseUrl}/api/counselors/createDocuments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Documents created:", response.data);
      toast.success("Documents uploaded successfully!");
      return true;
    } catch (error) {
      console.error("Document creation error:", error);
      toast.error("Failed to upload documents");
      return false;
    } finally {
      setDocumentsLoading(false);
    }
  };

  const updateDocuments = async () => {
    setDocumentsLoading(true);
    try {
      const formData = new FormData();

      if (workAuthDocument) {
        formData.append("workAuthDocument", workAuthDocument);
      }
      // if (ndaDocument) {
      //   formData.append("NDA", ndaDocument);
      // }
      if (profileImage) {
        formData.append("profileImage", profileImage);
      }
      if (isChecked) {
        formData.append("profile_consent", "1");
      } else {
        formData.append("profile_consent", "0");
      }

      // Add the document ID for update
      formData.append("id", docData.id);

      const response = await axios.post(
        `${baseUrl}/api/counselors/updateDocuments`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Documents updated:", response.data);
      toast.success("Documents updated successfully!");
      return true;
    } catch (error) {
      console.error("Error updating documents:", error);
      toast.error("Failed to update documents");
      return false;
    } finally {
      setDocumentsLoading(false);
    }
  };

  const handleDocumentsNext = async () => {
    // Validate required files
    if (!workAuthDocument && !workAuthUrl) {
      setWorkAuthError("Work Authorization document is required.");
      toast.error("Work Authorization document is required");
      return;
    }
    // if(!isChecked)
    // {

    //   toast.error("Please select checkbox for your consent");
    //   return;
    // }
    // Check if any files have been selected for upload or if there are changes
    const hasNewFiles = workAuthDocument || profileImage;
    // console.log("has files" + hasNewFiles);
    // Check if there are changes in existing documents
    const hasChanges =
      docData &&
      ((profileImage && !docData.profileImage) ||
        (workAuthDocument && !docData.workAuthDocument));
    // console.log("has files 2" + hasChanges);
    let success = false;
    if (docData && docData.id) {
      // Documents exist, check if we need to update
      // if (hasChanges) {
      //  setIsChecked(true);
      success = await updateDocuments();
      // Refresh document data after update to get latest URLs
      await fetchDocuments();
      // } else {
      //   // No changes to documents
      //   success = true;
      // }
    } else {
      // No existing documents, create new
      success = await createDocuments();
      // Fetch documents after creation to get the URLs
      await fetchDocuments();
    }

    if (success) {
      // Fetch bank details for the next step
      await getBankByCounselorId();
      nextStep();
    }
  };

  // STEP 3: Bank Details API Functions
  const handleBankChange = (e) => {
    setBankData({ ...bankData, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size must be 5MB or less");
      return;
    }
    if (e.target.files.length) {
      const src = URL.createObjectURL(e.target.files[0]);
      setImagePreview(src);
      setProfileImage(file);
    }
    if (!file) return;

    const imageUrl = URL.createObjectURL(file);
    setImagePreview(imageUrl); // show preview image
  };
  const handleFileChangeBank = (e) => {
    const file = e.target.files[0];
    if (file && file.size > MAX_FILE_SIZE) {
      toast.error("File size must be 5MB or less");
      return;
    }
    // if (e.target.files.length) {
    //   const src = URL.createObjectURL(e.target.files[0]);
    //   setImagePreview(src);
    //   setProfileImage(file);
    // }
    if (!file) return;

    // const imageUrl = URL.createObjectURL(file);
    // setImagePreview(imageUrl); // show preview image

    setBankData({ ...bankData, cancelledCheque: file });
  };
  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > MAX_FILE_SIZE) {
        setProfileError("Profile picture must be less than 5MB.");
        setProfileImage(null);
        setImagePreview(null);
        return;
      }

      // Check image dimensions
      const img = new Image();
      img.onload = () => {
        URL.revokeObjectURL(img.src); // Clean up
        if (img.width !== 150 || img.height !== 150) {
          setProfileError("Profile picture must be exactly 150x150 pixels.");
          setProfileImage(null);
          setImagePreview(null);
        } else {
          setProfileError("");
          setProfileImage(file);
          setImagePreview(URL.createObjectURL(file));
        }
      };
      img.onerror = () => {
        setProfileError("Invalid image file.");
        setProfileImage(null);
        setImagePreview(null);
      };
      img.src = URL.createObjectURL(file);
    }
  };

  // const handleFileChanges = (event) => {
  //   const files = Array.from(event.target.files);
  //   const newFileNames = files?.map((file) => file.name);
  //   setFileNames((prev) => [...prev, ...newFileNames]);
  // };

  const getBankByCounselorId = async () => {
    setBankLoading(true);
    try {
      const response = await axios.get(
        `${baseUrl}/api/counselors/getBankByCounsolerId`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const data = response.data?.data;

      console.log(data, "<<<<<<<<<<<<Bank api data::::::::::");

      if (data) {
        setBankData({
          bankName: data.bankName || "",
          routingNumber: data.RoutingNumber || "",
          accountNo: data.AccountNo || "",
          confirmAccountNo: data.AccountNo || "",
          accountType: data.AccountType || "",
          cancelledCheque: null,
        });
        setBankId(data.id); // Store ID for updates

        if (data.CancelledCheque) {
          setUploadedChequeUrl(`${baseUrl}${data.CancelledCheque}`); // <-- Store file URL from API
        }
      }
    } catch (error) {
      console.error("Error fetching bank details:", error);
      toast.error("Failed to load bank details");
    } finally {
      setBankLoading(false);
    }
  };

  const createBankDetails = async () => {
    setBankLoading(true);
    const formData = new FormData();
    formData.append("bankName", bankData.bankName);
    formData.append("RoutingNumber", bankData.routingNumber);
    formData.append("AccountNo", bankData.accountNo);
    formData.append("AccountType", bankData.accountType);

    if (bankData.cancelledCheque) {
      formData.append("CancelledCheque", bankData.cancelledCheque);
    }

    try {
      const response = await axios.post(
        `${baseUrl}/api/counselors/createBank`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Bank created successfully:", response.data);
      toast.success("Bank details saved successfully!");
      return true;
    } catch (error) {
      console.error("Error creating bank details:", error);
      toast.error("Failed to save bank details");
      return false;
    } finally {
      setBankLoading(false);
    }
  };

  const updateBankDetails = async () => {
    setBankLoading(true);
    const formData = new FormData();
    formData.append("bankName", bankData.bankName);
    formData.append("RoutingNumber", bankData.routingNumber);
    formData.append("AccountNo", bankData.accountNo);
    formData.append("AccountType", bankData.accountType);
    formData.append("id", bankId);

    if (bankData.cancelledCheque) {
      formData.append("CancelledCheque", bankData.cancelledCheque);
    }

    try {
      const response = await axios.post(
        `${baseUrl}/api/counselors/updateBank`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      console.log("Bank updated successfully:", response.data);
      toast.success("Bank details updated successfully!");
      return true;
    } catch (error) {
      console.error("Error updating bank details:", error);
      toast.error("Failed to update bank details");
      return false;
    } finally {
      setBankLoading(false);
    }
  };

  const handleBankNext = async () => {
    // Validate bank details
    // if (
    //   !bankData.bankName ||
    //   !bankData.routingNumber ||
    //   !bankData.accountNo ||
    //   !bankData.accountType
    // ) {
    //   toast.error("Please fill in all required bank details");
    //   return;
    // }

    if (bankData.accountNo !== bankData.confirmAccountNo) {
      toast.error("Account numbers do not match");
      return;
    }

    // First get the latest bank data
    await getBankByCounselorId();

    let success = false;
    if (bankId) {
      // Bank details exist, update them
      success = await updateBankDetails();
    } else {
      // No existing bank details, create new
      success = await createBankDetails();
    }

    if (success) {
      nextStep(); // Move to completed step
    }
  };

  // Render loading spinner
  const renderLoader = () => (
    <div
      className="d-flex justify-content-center align-items-center h-100 w-100 position-absolute top-0 left-0"
      style={{ backgroundColor: "rgba(255, 255, 255, 0.8)" }}
    >
      <div className="text-center">
        <Loader2 className="animate-spin h-10 w-10 mb-2 mx-auto" />
        <span>Loading...</span>
      </div>
    </div>
  );

  useEffect(() => {
    if (stateName) {
      const stateCities = City.getCitiesOfState(country, stateName);
      setCities(stateCities);
    }
    // }
  }, [country, stateName]);

  // Policies

  const handleOpenPolicy = async (fieldName, title) => {
    setModalTitle(title);
    setShowModal(true);
    setPolicyLoading(true);
    setSelectedPolicy("");

    try {
      const response = await axios.get(`${baseUrl}/api/policy/allPolicy`, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const policy = response.data?.data?.[fieldName];
      if (policy) {
        setSelectedPolicy(policy);
      } else {
        toast.warn("Policy content not available.");
      }
    } catch (error) {
      console.error("Error fetching policy:", error);
      toast.error("Failed to fetch policy.");
    } finally {
      setPolicyLoading(false);
    }
  };

  return (
    <div className="col-md-12">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="card">
        <div className="card-body">
          {currentStep !== 5 && (
            <h6 className="d-flex justify-content-center text-primary-600">
              Complete your profile for review and approval by the
              Dreamdor team!!
            </h6>
          )}

          {/* Form Wizard Start */}
          <div className="form-wizard">
            <form action="#" method="post">
              <div className="form-wizard-header overflow-x-auto scroll-sm pb-8 my-32">
                <ul className="list-unstyled form-wizard-list">
                  <li
                    className={`form-wizard-list__item
                      ${[2, 3, 4, 5].includes(currentStep) && "activated"}
                    ${currentStep === 1 && "active"} `}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count">1</span>
                    </div>
                    <span className="text text-xs fw-semibold">
                      Personal Details
                    </span>
                  </li>
                  <li
                    className={`form-wizard-list__item
                      ${[3, 4, 5].includes(currentStep) && "activated"}
                    ${currentStep === 2 && "active"} `}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count">2</span>
                    </div>
                    <span className="text text-xs fw-semibold">
                      Address Details
                    </span>
                  </li>
                  <li
                    className={`form-wizard-list__item
                      ${[4, 5].includes(currentStep) && "activated"}
                    ${currentStep === 3 && "active"} `}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count">3</span>
                    </div>
                    <span className="text text-xs fw-semibold">Documents</span>
                  </li>
                  <li
                    className={`form-wizard-list__item
                      ${[5].includes(currentStep) && "activated"}
                    ${currentStep === 4 && "active"} `}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count">4</span>
                    </div>
                    <span className="text text-xs fw-semibold">
                      Bank Details
                    </span>
                  </li>
                  <li
                    className={`form-wizard-list__item
                      ${[5].includes(currentStep) && "activated"}
                    ${currentStep === 5 && "active"} `}
                  >
                    <div className="form-wizard-list__line">
                      <span className="count">5</span>
                    </div>
                    <span className="text text-xs fw-semibold">Completed</span>
                  </li>
                </ul>
              </div>
              {/* Step 1: Personal Details */}
              <fieldset
                className={`wizard-fieldset ${
                  currentStep === 1 && "show"
                } position-relative`}
              >
                {personalDetailsLoading && renderLoader()}
                <div>
                  <div className="row gy-3">
                    {/* First Name */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        First Name<span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="firstName"
                        value={personalDetails.firstName}
                        readOnly
                      />
                    </div>

                    {/* Last Name */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Last Name<span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="lastName"
                        value={personalDetails.lastName}
                        readOnly
                      />
                    </div>

                    {/* Email */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Email<span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="email"
                        className="form-control"
                        name="email"
                        value={personalDetails.email}
                        readOnly
                      />
                    </div>

                    {/* Country Code + Phone */}
                    <div className="col-sm-6">
                      <div className="row">
                        <div className="col-sm-4">
                          <label className="form-label">
                            Country Code
                            <span className="text-danger-600">*</span>
                          </label>
                          <select
                            className="form-control"
                            name="countryCode"
                            value={personalDetails.countryCode}
                            disabled
                          >
                            <option value="">Code</option>
                            <option value="+1">+1</option>
                            <option value="+91">+91</option>
                            <option value="+44">+44</option>
                          </select>
                        </div>
                        <div className="col-sm-8">
                          <label className="form-label">
                            Phone<span className="text-danger-600">*</span>
                          </label>
                          <input
                            type="number"
                            className="form-control"
                            name="phone"
                            value={personalDetails.phone}
                            readOnly
                          />
                        </div>
                      </div>
                    </div>

                    {/* University */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        University<span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        name="universityName"
                        value={personalDetails.universityName}
                        readOnly
                      />
                    </div>

                    {/* SAT/ACT Score */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        SAT/ACT Score<span className="text-danger-600">*</span>
                      </label>
                      <input
                        type="number"
                        className="form-control"
                        name="satActScore"
                        placeholder="Enter SAT/ACT Score"
                        value={personalDetails.satActScore}
                        onChange={handleChanges}
                      />
                    </div>

                    {/* Major Course */}
                    <div className="col-sm-6">
                      <label className="form-label">Major Course</label>
                      <input
                        type="text"
                        className="form-control"
                        name="majorCourse"
                        placeholder="Enter Major Course"
                        value={personalDetails.majorCourse}
                        onChange={handleChanges}
                      />
                    </div>

                    {/* Service Type */}
                    <div className="col-sm-6">
                      <label className="form-label">
                        Service Type<span className="text-danger-600">*</span>
                      </label>
                      <select
                        className="form-control"
                        name="interestedIn"
                        value={personalDetails.interestedIn}
                        onChange={handleChanges}
                      >
                        <option value="Essay Counseling">
                          Essay Counseling
                        </option>
                        <option value="College Advising">
                          College Advising
                        </option>
                        <option value="Comprehensive Service (Essay, College Advising)">
                          Comprehensive Service (Essay, College Advising)
                        </option>
                        {/* <option value="AI/ML Engineering">AI/ML Engineering</option> */}
                        {/* <option value="ACT/SAT Prep">ACT/SAT Prep</option> */}
                        <option value="Full Support Counseling">
                          Full Support Counseling
                        </option>
                        {/* <option value="Essay + General Counseling">
                          Essay + General Counseling
                        </option> */}
                      </select>
                    </div>

                    {/* Testimony */}
                    <div className="col-12 mb-20">
                      <label className="form-label">
                        Testimony (200 Words Min)
                        <span className="text-danger-600">*</span>
                      </label>
                      <textarea
                        className="form-control"
                        name="testimony"
                        placeholder="Enter Testimony"
                        rows={4}
                        value={personalDetails.testimony}
                        onChange={handleChanges}
                      />
                    </div>
                  </div>

                  <div className="flex justify-center items-center text-center mt-4">
                    <p className="text-sm max-w-4xl px-2">
                      By clicking the Submit/Proceed button, I confirm that I
                      have read and agree to the{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "terms_and_conditions",
                            "Terms & Conditions"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Terms & Conditions
                      </span>
                      ,{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy("privacy_policy", "Privacy Policy")
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Privacy Policy
                      </span>{" "}
                      and{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "reschedule_cancellation",
                            "Reschedule Policy"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Reschedule Policy
                      </span>{" "}
                      of Dream Door LLC.
                    </p>
                  </div>
                  {/* Next Button */}
                  <div className="form-group text-end mt-3">
                    <button
                      onClick={handlePersonalDetailsNext}
                      type="button"
                      className="form-wizard-next-btn btn btn-primary-600 px-32"
                      disabled={personalDetailsLoading}
                    >
                      {personalDetailsLoading ? (
                        <span className="d-flex align-items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Processing...
                        </span>
                      ) : (
                        "Proceed"
                      )}
                    </button>
                  </div>
                </div>
              </fieldset>

              {/* Step 2: Personal Details */}
              <fieldset
                className={`wizard-fieldset ${
                  currentStep === 2 && "show"
                } position-relative`}
              >
                {personalDetailsLoading && renderLoader()}
                <div className="row gy-3">
                  {/* Country */}
                  <div className="col-sm-6 col-md-3">
                    <label className="form-label">
                      Country<span className="text-danger-600">*</span>
                    </label>
                    <select className="form-select" value={country} disabled>
                      {countries.map((c) => (
                        <option key={c.isoCode} value={c.isoCode}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* State */}
                  <div className="col-sm-6 col-md-3">
                    <label className="form-label">
                      State<span className="text-danger-600">*</span>
                    </label>
                    <select
                      className="form-select wizard-required"
                      value={stateName}
                      onChange={(e) => setStateName(e.target.value)}
                      required
                    >
                      <option value="">Select State</option>
                      {states.map((s) => (
                        <option key={s.isoCode} value={s.isoCode}>
                          {s.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* City */}
                  <div className="col-sm-6 col-md-3">
                    <label className="form-label">
                      City<span className="text-danger-600">*</span>
                    </label>
                    <select
                      className="form-select wizard-required"
                      value={city}
                      onChange={(e) => setCity(e.target.value)}
                      required
                    >
                      <option value="">Select City</option>
                      {cities.map((c) => (
                        <option key={c.name} value={c.name}>
                          {c.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Zip Code */}
                  <div className="col-sm-6 col-md-3">
                    <label className="form-label">
                      Zip Code<span className="text-danger-600">*</span>
                    </label>
                    <input
                      type="number"
                      className="form-control wizard-required"
                      placeholder="Enter Zip Code"
                      value={zipCode}
                      onChange={(e) => setZipCode(e.target.value)}
                      required
                    />
                  </div>

                  {/* Address */}
                  <div className="col-4">
                    <label className="form-label">
                      Address<span className="text-danger-600">*</span>
                    </label>
                    <textarea
                      type="text"
                      className="form-control wizard-required"
                      placeholder="Enter Address"
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      required
                    />
                  </div>
                  <div className="col-8">
                    <div className="row align-items-center">
                      <div className="col-3">
                        <label className="form-label mb-0">
                          Are you an entity?
                          <span className="text-danger-600">*</span>
                        </label>
                        <select
                          className="form-select wizard-required"
                          value={isEntity}
                          onChange={(e) => {
                            setIsEntity(e.target.value);
                            setIdNumber("");
                          }}
                          required
                        >
                          <option value="">Select</option>
                          <option value="yes">Yes</option>
                          <option value="no">No</option>
                        </select>
                      </div>

                      {(isEntity === "yes" || isEntity === "no") &&
                        renderFormattedInput()}
                      <p className="text-sm" style={{ color: "#CA8A04" }}>
                        Your SSN/EIN is required to generate a 1099 form for tax
                        reporting purposes, as mandated by U.S. tax regulations.
                        This information is securely stored and used only for
                        compliance purposes.
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center items-center text-center mt-4">
                    <p className="text-sm max-w-4xl px-2">
                      By clicking the Submit/Proceed button, I confirm that I
                      have read and agree to the{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "terms_and_conditions",
                            "Terms & Conditions"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Terms & Conditions
                      </span>
                      ,{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy("privacy_policy", "Privacy Policy")
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Privacy Policy
                      </span>{" "}
                      and{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "reschedule_cancellation",
                            "Reschedule Policy"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Reschedule Policy
                      </span>{" "}
                      of Dream Door LLC.
                    </p>
                  </div>

                  {/* Next Button */}
                  <div className="form-group d-flex align-items-center justify-content-between w-100">
                    <button
                      onClick={prevStep}
                      type="button"
                      className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddressDetailsNext}
                      type="button"
                      className="form-wizard-next-btn btn btn-primary-600 px-32"
                      disabled={personalDetailsLoading}
                    >
                      {personalDetailsLoading ? (
                        <span className="d-flex align-items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Processing...
                        </span>
                      ) : (
                        "Proceed"
                      )}
                    </button>
                  </div>

                  {/* <div className="form-group d-flex align-items-center justify-content-end gap-8">
                    <button
                      onClick={prevStep}
                      type="button"
                      className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                    >
                      Back
                    </button>
                    <button
                      onClick={handleAddressDetailsNext}
                      type="button"
                      className="form-wizard-next-btn btn btn-primary-600 px-32"
                      disabled={personalDetailsLoading}
                    >
                      {personalDetailsLoading ? (
                        <span className="d-flex align-items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Processing...
                        </span>
                      ) : (
                         "Proceed"
                      )}
                    </button>
                  </div> */}
                </div>
              </fieldset>

              {/* Step 3: Documents */}
              <fieldset
                className={`wizard-fieldset ${
                  currentStep === 3 ? "show" : ""
                } position-relative`}
              >
                {documentsLoading && renderLoader()}
                <div className="row gy-3">
                  {/* Profile Picture Preview */}
                  {/* {(previewUrl || profileImageUrl) && (
                    <div className="col-12">
                      <div className="mt-2 d-flex justify-content-center">
                        <img
                          src={previewUrl || profileImageUrl}
                          alt="Profile Preview"
                          style={{
                            width: "150px",
                            height: "150px",
                            objectFit: "cover",
                            borderRadius: "8px",
                            border: "1px solid #ccc",
                          }}
                        />
                      </div>
                    </div>
                  )} */}
                  {/* Profile Picture */}
                  <div className="col-md-6">
                    <div className="card h-100 p-0">
                      <div className="card-header border-bottom bg-base py-16 px-24">
                        <label className="form-label mb-0">
                          Profile Picture (upto 5MB)
                        </label>
                      </div>
                      <div className="card-body p-24">
                        <div className="upload-image-wrapper d-flex align-items-center gap-3">
                          {/* Image preview section */}
                          {imagePreview ? (
                            <div className="uploaded-img position-relative h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50">
                              <button
                                type="button"
                                onClick={removeImage}
                                className="uploaded-img__remove position-absolute top-0 end-0 z-1 text-2xxl line-height-1 me-8 mt-8 d-flex"
                                aria-label="Remove uploaded image"
                              >
                                <Icon
                                  icon="radix-icons:cross-2"
                                  className="text-xl text-danger-600"
                                ></Icon>
                              </button>
                              <img
                                id="uploaded-img__preview"
                                className="w-100 h-100 object-fit-cover"
                                src={imagePreview}
                                alt="Preview"
                              />
                            </div>
                          ) : (
                            <label
                              className="upload-file h-120-px w-120-px border input-form-light radius-8 overflow-hidden border-dashed bg-neutral-50 bg-hover-neutral-200 d-flex align-items-center flex-column justify-content-center gap-1"
                              htmlFor="upload-file"
                            >
                              <Icon
                                icon="solar:camera-outline"
                                className="text-xl text-secondary-light"
                              ></Icon>
                              <span className="fw-semibold text-secondary-light">
                                Upload
                              </span>
                            </label>
                          )}

                          {/* Always render the input, but hide it */}
                          <input
                            id="upload-file"
                            type="file"
                            onChange={handleFileChange}
                            hidden
                            ref={fileInputRef}
                            accept="image/*" // Optional: restrict to image files
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* Work Authorization */}

                  <div className="col-md-6">
                    <div className="card h-100 p-0">
                      <div className="card-header border-bottom bg-base py-16 px-24">
                        <label className="form-label mb-0">
                          Work Authorization Document (upto 5MB)
                          <span className="text-danger-600">*</span>
                        </label>
                      </div>
                      <div className="card-body p-24">
                        <label
                          htmlFor="file-upload-name"
                          className="mb-16 border border-neutral-600 fw-medium text-secondary-light px-16 py-12 radius-12 d-inline-flex align-items-center gap-2 bg-hover-neutral-200"
                        >
                          <Icon
                            icon="solar:upload-linear"
                            className="text-xl"
                          ></Icon>
                          Click to upload
                          <input
                            type="file"
                            className="form-control w-auto mt-24 form-control-lg"
                            id="file-upload-name"
                            hidden
                            onChange={handleWorkAuthChange}
                          />
                        </label>

                        {fileNames.length > 0 && (
                          <ul
                            id="uploaded-img-names"
                            className="show-uploaded-img-name"
                          >
                            {fileNames?.map((fileName, index) => (
                              <li
                                key={index}
                                className="uploaded-image-name-list text-primary-600 fw-semibold d-flex align-items-center gap-2"
                              >
                                <Icon
                                  icon="ph:link-break-light"
                                  className="text-xl text-secondary-light"
                                ></Icon>
                                {fileName}
                                <Icon
                                  icon="radix-icons:cross-2"
                                  className="remove-image text-xl text-secondary-light text-hover-danger-600"
                                  onClick={() => removeFileName(fileName)}
                                ></Icon>
                              </li>
                            ))}
                          </ul>
                        )}
                        {workAuthUrl && (
                          <div className="mt-2">
                            <li className="uploaded-image-name-list text-primary-600 d-flex align-items-center gap-2">
                              <Icon
                                icon="ph:link-break-light"
                                className="text-xl text-secondary-light"
                              ></Icon>
                              <a
                                href={workAuthUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View Uploaded Work Authorization Document
                              </a>
                            </li>
                            {/* <a
                              href={workAuthUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              View Uploaded Work Authorization Document
                            </a> */}
                          </div>
                        )}
                        {workAuthError && (
                          <small className="text-danger">{workAuthError}</small>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* <div className="col-3">
                    <label className="form-label">
                      Work Authorization Document (5MB)*
                    </label>
                    <label
                      htmlFor="file-upload-name"
                      className="mb-16 border border-neutral-600 fw-medium text-secondary-light px-16 py-12 radius-12 d-inline-flex align-items-center gap-2 bg-hover-neutral-200"
                    >
                      <Icon
                        icon="solar:upload-linear"
                        className="text-xl"
                      ></Icon>
                      Click to upload
                      <input
                        type="file"
                        className="form-control w-auto mt-24 form-control-lg"
                        id="file-upload-name"
                        hidden
                        accept="application/pdf"
                        onChange={handleWorkAuthChange}
                        required
                      />
                    </label>
                    {fileNames.length > 0 && (
                      <ul
                        id="uploaded-img-names"
                        className="show-uploaded-img-name"
                      >
                        {fileNames?.map((fileName, index) => (
                          <li
                            key={index}
                            className="uploaded-image-name-list text-primary-600 fw-semibold d-flex align-items-center gap-2"
                          >
                            <Icon
                              icon="ph:link-break-light"
                              className="text-xl text-secondary-light"
                            ></Icon>
                            {fileName}
                            <Icon
                              icon="radix-icons:cross-2"
                              className="remove-image text-xl text-secondary-light text-hover-danger-600"
                              onClick={() => removeFileName(fileName)}
                            ></Icon>
                          </li>
                        ))}
                      </ul>
                    )}
                    <input
                      type="file"
                      className="form-control wizard-required"
                      accept="application/pdf"
                      onChange={handleWorkAuthChange}
                      required
                    />
                    {workAuthUrl && (
                      <div className="mt-2">
                        <a
                          href={workAuthUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Uploaded Work Authorization Document
                        </a>
                      </div>
                    )}
                    {workAuthError && (
                      <small className="text-danger">{workAuthError}</small>
                    )}
                  </div> */}
                  <div className="col-3"></div>
                  {/* NDA */}
                  {/* <div className="col-10">
                    <label className="form-label">NDA (max 3MB)*</label>
                    <input
                      type="file"
                      className="form-control wizard-required"
                      accept="application/pdf"
                      onChange={handleNdaChange}
                      required
                    />
                    {ndaUrl && (
                      <div className="mt-2">
                        <a
                          href={ndaUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          View Uploaded NDA
                        </a>
                      </div>
                    )}
                    {ndaError && (
                      <small className="text-danger">{ndaError}</small>
                    )}
                  </div>
                  <div className="col-2">
                    <label className="form-label">Download (PDF) *</label>
                    <button className="btn btn-primary" onClick={downloadNDA}>
                      Download
                    </button>
                  </div> */}
                  <div className="d-flex align-items-center gap-10">
                    <div className="form-check style-check d-flex align-items-center">
                      <input
                        className="form-check-input radius-4 border input-form-dark"
                        type="checkbox"
                        id="agreeTerms" // Unique ID for the checkbox.
                        name="agreeTerms" // Name attribute for form submission.
                        checked={isChecked} // Controls the checked state of the checkbox.
                        onChange={handleCheckboxChange}
                        required
                      />
                    </div>
                    <label className="form-label mb-0">
                      {" "}
                      I consent to share my picture on the DreamDor platform.
                    </label>
                  </div>

                  {/* Navigation Buttons */}

                  <div className="flex justify-center items-center text-center mt-4">
                    <p className="text-sm max-w-4xl px-2">
                      By clicking the Submit/Proceed button, I confirm that I
                      have read and agree to the{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "terms_and_conditions",
                            "Terms & Conditions"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Terms & Conditions
                      </span>
                      ,{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy("privacy_policy", "Privacy Policy")
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Privacy Policy
                      </span>{" "}
                      and{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "reschedule_cancellation",
                            "Reschedule Policy"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Reschedule Policy
                      </span>{" "}
                      of Dream Door LLC.
                    </p>
                  </div>
                  <div className="form-group d-flex align-items-center justify-content-between w-100">
                    <button
                      onClick={prevStep}
                      type="button"
                      className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                      disabled={documentsLoading}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleDocumentsNext}
                      type="button"
                      className="form-wizard-next-btn btn btn-primary-600 px-32"
                      disabled={documentsLoading}
                    >
                      {documentsLoading ? (
                        <span className="d-flex align-items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Processing...
                        </span>
                      ) : (
                        "Proceed"
                      )}
                    </button>
                  </div>
                </div>
              </fieldset>

              {/* Step 4: Bank Details */}
              <fieldset
                className={`wizard-fieldset ${
                  currentStep === 4 && "show"
                } position-relative`}
              >
                {bankLoading && renderLoader()}
                <div className="row gy-3">
                  <div className="flex justify-center items-center text-center">
                    <p
                      className="text-sm justify-content-center"
                      style={{ color: "#CA8A04" }}
                    >
                      Please enter your bank details carefully. This information
                      will be used for weekly session payouts.
                    </p>
                  </div>

                  {/* Bank Name */}
                  <div className="col-6">
                    <label className="form-label">Bank Name</label>
                    <input
                      type="text"
                      name="bankName"
                      value={bankData.bankName}
                      onChange={handleBankChange}
                      className="form-control"
                    />
                  </div>

                  {/* Routing Number */}
                  <div className="col-6">
                    <label className="form-label">Routing Number</label>
                    <input
                      type="number"
                      name="routingNumber"
                      value={bankData.routingNumber}
                      onChange={handleBankChange}
                      className="form-control"
                    />
                  </div>

                  {/* Bank Account Number */}
                  <div className="col-6">
                    <label className="form-label">Bank A/C No.</label>
                    <input
                      type="password"
                      name="accountNo"
                      value={bankData.accountNo}
                      onChange={handleBankChange}
                      className="form-control"
                    />
                  </div>

                  {/* Confirm Bank Account Number */}
                  <div className="col-6">
                    <label className="form-label">Confirm Bank A/C No.</label>
                    <input
                      type="number"
                      name="confirmAccountNo"
                      value={bankData.confirmAccountNo}
                      onChange={handleBankChange}
                      className="form-control"
                    />
                  </div>

                  {/* Cancelled Cheque Upload and Account Type Row */}
                  <div className="row">
                    {/* Cancelled Cheque Upload */}
                    <div className="col-6">
                      <label className="form-label">
                        Upload Cancelled Cheque (upto 5MB)
                      </label>
                      <input
                        type="file"
                        id="chequeUpload"
                        className="form-control"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={handleFileChangeBank}
                      />

                      {/* Show uploaded file link below input */}
                      {uploadedChequeUrl && (
                        <div className="form-label text-decoration-underline">
                          <a
                            href={uploadedChequeUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            View Existing Cancelled Cheque
                          </a>
                        </div>
                      )}
                    </div>

                    {/* Account Type */}
                    <div className="col-6">
                      <label className="form-label">Account Type</label>
                      <select
                        name="accountType"
                        value={bankData.accountType}
                        onChange={handleBankChange}
                        className="form-select"
                      >
                        <option value="">Select Account Type</option>
                        <option value="savings">Savings</option>
                        <option value="checking">Checking</option>
                      </select>
                    </div>
                  </div>

                  <div className="flex justify-center items-center text-center mt-4">
                    <p className="text-sm max-w-4xl px-2">
                      By clicking the Submit/Proceed button, I confirm that I
                      have read and agree to the{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "terms_and_conditions",
                            "Terms & Conditions"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Terms & Conditions
                      </span>
                      ,{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy("privacy_policy", "Privacy Policy")
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Privacy Policy
                      </span>{" "}
                      and{" "}
                      <span
                        onClick={() =>
                          handleOpenPolicy(
                            "reschedule_cancellation",
                            "Reschedule Policy"
                          )
                        }
                        className="text-blue-600 underline cursor-pointer"
                        style={{ color: "blue" }}
                      >
                        Reschedule Policy
                      </span>{" "}
                      of Dream Door LLC.
                    </p>
                  </div>
                  {/* Navigation Buttons */}
                  <div className="form-group d-flex align-items-center justify-content-between w-100">
                    <button
                      onClick={prevStep}
                      type="button"
                      className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                      disabled={bankLoading}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleBankNext}
                      type="button"
                      disabled={bankLoading}
                      className="form-wizard-next-btn btn btn-primary-600 px-32"
                    >
                      {bankLoading ? (
                        <span className="d-flex align-items-center">
                          <Loader2 className="animate-spin h-4 w-4 mr-2" />
                          Processing...
                        </span>
                      ) : (
                        "Proceed"
                      )}
                    </button>
                  </div>
                </div>
              </fieldset>

              {/* Step 5: Completed */}
              <fieldset
                className={`wizard-fieldset ${currentStep === 5 && "show"} `}
              >
                <div className="text-center mb-40">
                  <img
                    src="assets/images/gif/success-img3.gif"
                    alt=""
                    className="gif-image mb-24"
                  />
                  <h6 className="text-md text-neutral-600">Congratulations!</h6>
                  <p className=" text-md mb-0 text-primary-600">
                    Please allow our team some time to review and verify your
                    profile. We will notify you once your profile has been
                    verified and is active.
                  </p>
                </div>
                <div className="form-group d-flex align-items-center justify-content-end gap-8">
                  <button
                    onClick={prevStep}
                    type="button"
                    className="form-wizard-previous-btn btn btn-neutral-500 border-neutral-100 px-32"
                  >
                    Back
                  </button>
                </div>
              </fieldset>
            </form>
          </div>
        </div>
      </div>
      <Modal
        open={showModal}
        onClose={() => setShowModal(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description"
      >
        <Box sx={style}>
          <IconButton
            onClick={() => setShowModal(false)}
            sx={{ position: "absolute", top: 8, right: 8 }}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>

          <Typography variant="h6" mb={2}>
            {modalTitle}
          </Typography>

          {policyLoading ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "200px",
              }}
            >
              <CircularProgress />
            </Box>
          ) : (
            <Box dangerouslySetInnerHTML={{ __html: selectedPolicy }} />
          )}
        </Box>
      </Modal>
    </div>
  );
};

export default CounselorProfilePage;
